import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { getPayrollRosterApiErrorMessage } from "@/payroll/roster/api-errors";
import { AccessComparison } from "./access-comparison";
import {
  useApplyAccessDraft,
  useDiscardAccessDraft,
  useReviewAccessDraft,
} from "./query";
import type { AccessDraft, DraftReview, WorkbookAccessSettings } from "./types";

export const DraftControls = ({
  businessId,
  settings,
  disabled,
  onDiscarded,
}: {
  businessId: string;
  settings: WorkbookAccessSettings;
  disabled: boolean;
  onDiscarded: () => void;
}) => {
  const [review, setReview] = useState<DraftReview | null>(null);
  const [discarding, setDiscarding] = useState<AccessDraft | null>(null);
  const reviewDraft = useReviewAccessDraft(businessId);
  const apply = useApplyAccessDraft(businessId);
  const discard = useDiscardAccessDraft(businessId);
  const draft = settings.draft;
  const current =
    review?.draft_id === draft?.id &&
    review?.reviewed_digest === draft?.content_digest &&
    !draft?.stale;
  const reportError = (error: unknown) =>
    toast.error(getPayrollRosterApiErrorMessage(error));
  return (
    <>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={3}
        className="w-full rounded-lg border bg-neutral px-4 py-3"
      >
        <Stack align="start" gap={1}>
          <Text variant="label">
            {draft
              ? `Draft ${draft.version_number} · Not applied`
              : "Applied access"}
          </Text>
          <Text variant="caption" tone="muted">
            {draft?.stale
              ? "Applied access changed. Discard this draft and start again."
              : draft
                ? "Changes for all people and sheets are saved in this shared draft."
                : "Save changes to a draft, then review and apply them together."}
          </Text>
        </Stack>
        {draft && (
          <Stack direction="row" gap={2}>
            <Button
              variant="outline"
              disabled={disabled || apply.isPending || discard.isPending}
              onClick={() => setDiscarding(draft)}
            >
              Discard draft
            </Button>
            <Button
              disabled={
                disabled ||
                draft.stale ||
                reviewDraft.isPending ||
                discard.isPending
              }
              loading={reviewDraft.isPending}
              onClick={async () => {
                try {
                  setReview(await reviewDraft.mutateAsync(draft));
                } catch (error) {
                  reportError(error);
                }
              }}
            >
              Review changes
            </Button>
          </Stack>
        )}
      </Stack>
      <Dialog
        open={review !== null}
        onOpenChange={(open) => {
          if (!open && !apply.isPending) setReview(null);
        }}
      >
        <DialogContent className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review workbook access changes</DialogTitle>
            <DialogDescription>
              Apply all changes in this draft at the same time.
            </DialogDescription>
          </DialogHeader>
          {review && (
            <Stack align="start" gap={4} className="min-h-0 overflow-auto">
              {!current && (
                <Alert color="danger">
                  This draft changed. Close this review and review the latest
                  draft.
                </Alert>
              )}
              {!review.preview.valid && (
                <Alert color="danger">{review.preview.errors.join(". ")}</Alert>
              )}
              {!review.preview.workbook && (
                <Text variant="caption" tone="muted">
                  No workbook is available. This review shows permission
                  changes. Sheet and technician results cannot be tested yet.
                </Text>
              )}
              {review.preview.people
                .filter((person) =>
                  review.preview.changed_user_ids.includes(person.user_id),
                )
                .map((person) => (
                  <Stack
                    key={person.user_id}
                    align="start"
                    gap={1}
                    className="w-full border-b pb-3"
                  >
                    <Text variant="label">{person.name || person.email}</Text>
                    <Text variant="caption" tone="muted">
                      {person.email}
                    </Text>
                    {person.added.map((change) => (
                      <Text key={`add-${change}`} variant="caption">
                        Add: {change}
                      </Text>
                    ))}
                    {person.removed.map((change) => (
                      <Text key={`remove-${change}`} variant="caption">
                        Remove: {change}
                      </Text>
                    ))}
                    {person.changed.map((change) => (
                      <Text key={`change-${change}`} variant="caption">
                        Change: {change}
                      </Text>
                    ))}
                    {person.after && <AccessComparison person={person} />}
                    {person.loses_all_access && (
                      <Text variant="caption">
                        This person will have no workbook access.
                      </Text>
                    )}
                  </Stack>
                ))}
              {review.preview.key_changes.map((key) => (
                <Stack
                  key={key.key}
                  align="start"
                  gap={1}
                  className="w-full border-b pb-3"
                >
                  <Text variant="label">{key.name_after}</Text>
                  {key.name_before !== key.name_after && (
                    <Text variant="caption">
                      Rename: {key.name_before} → {key.name_after}
                    </Text>
                  )}
                  {key.team_before !== key.team_after && (
                    <Text variant="caption">
                      Team: {key.team_after ? "Enabled" : "Disabled"}
                    </Text>
                  )}
                </Stack>
              ))}
              {review.preview.team_scope_change && (
                <Text>{review.preview.team_scope_change}</Text>
              )}
            </Stack>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={apply.isPending}
              onClick={() => setReview(null)}
            >
              Back to draft
            </Button>
            <Button
              disabled={
                !review || !current || !review.preview.valid || apply.isPending
              }
              loading={apply.isPending}
              onClick={async () => {
                if (!review || !current) return;
                try {
                  await apply.mutateAsync(review);
                  setReview(null);
                  toast.success("Workbook access applied.");
                } catch (error) {
                  reportError(error);
                }
              }}
            >
              Apply all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={discarding !== null}
        onOpenChange={(open) => {
          if (!open && !discard.isPending) setDiscarding(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard shared draft?</DialogTitle>
            <DialogDescription>
              This removes all saved draft changes for people and sheets.
              Applied access stays the same.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={discard.isPending}
              onClick={() => setDiscarding(null)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={discard.isPending}
              loading={discard.isPending}
              onClick={async () => {
                if (!discarding) return;
                try {
                  await discard.mutateAsync(discarding);
                  onDiscarded();
                  setDiscarding(null);
                } catch (error) {
                  reportError(error);
                }
              }}
            >
              Discard draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
